import { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { WithTranslation, withTranslation } from 'react-i18next';

import { IQuestionary } from '@core/assistant/providers/scriptPlayer/intefraces';
import { useStyles, getOptionStyle } from './styles';

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

interface IQuizScreenProps extends WithTranslation {
  questionText: string;
  questionary: IQuestionary;
  questionNumber?: number;
  totalQuestions?: number;
  maxOptions?: number;
  onConfirm: (selectedOptions: number[]) => void;
}

const QuizScreen = ({
  questionText,
  questionary,
  questionNumber,
  totalQuestions,
  onConfirm,
  t: translate,
}: IQuizScreenProps) => {
  const classes = useStyles();
  const isMulti = questionary.selectionMode === 'multiple';
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [left, setLeft] = useState('-9999px');

  useLayoutEffect(() => {
    const anchor = anchorRef.current;

    if (!anchor) {
      return;
    }

    const update = () => {
      const rect = anchor.getBoundingClientRect();
      const quizWidth = 300;
      const gap = 16;
      setLeft(`${Math.max(8, rect.left - quizWidth - gap)}px`);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const showProgress = totalQuestions !== undefined && totalQuestions > 1;
  const progress = showProgress
    ? ((questionNumber ?? 1) / totalQuestions!) * 100
    : 0;

  const correctAnswerText = questionary.options
    .filter((_, i) => questionary.correctAnswers.includes(i))
    .join(', ');

  const hasCorrectAnswers = questionary.correctAnswers.length > 0;

  const isCorrect =
    hasCorrectAnswers &&
    selectedOptions.length === questionary.correctAnswers.length &&
    selectedOptions.every((i) => questionary.correctAnswers.includes(i));

  const handleToggle = (i: number) => {
    if (confirmed) {
      return;
    }

    if (isMulti) {
      setSelectedOptions((prev) =>
        prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i],
      );
    } else {
      setSelectedOptions([i]);
    }
  };

  const handleConfirm = () => {
    if (selectedOptions.length === 0) {
      return;
    }
    setConfirmed(true);
  };

  const questionLabel = showProgress
    ? `${translate('assistant.quiz.question')} ${questionNumber}`
    : translate('assistant.quiz.question');

  return (
    <>
      <span
        ref={anchorRef}
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
        }}
      />
      {createPortal(
        <Box sx={{ ...classes.container, left }}>
          {/* Header */}
          <Box sx={classes.header}>
            <Typography sx={classes.headerText}>
              {translate('assistant.quiz.title')}
            </Typography>
            {showProgress && (
              <Typography sx={classes.headerText}>
                {questionNumber} / {totalQuestions}
              </Typography>
            )}
          </Box>

          {/* Progress bar */}
          {showProgress && (
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={classes.progressBar}
            />
          )}

          {/* Question card */}
          <Box sx={classes.questionCard}>
            <Typography sx={classes.questionLabel}>
              {questionLabel}
              {isMulti &&
                ` · ${translate('assistant.quiz.multipleChoiceTitle')}`}
            </Typography>
            <Typography sx={classes.questionText}>{questionText}</Typography>
          </Box>

          {/* Options */}
          <Box sx={classes.optionsList}>
            {questionary.options.map((option, i) => {
              const isSelected = selectedOptions.includes(i);
              const optStyle = getOptionStyle(
                i,
                confirmed,
                isSelected,
                questionary.correctAnswers,
                hasCorrectAnswers,
              );
              const showCheck =
                confirmed &&
                hasCorrectAnswers &&
                questionary.correctAnswers.includes(i);
              const showCross =
                confirmed &&
                isSelected &&
                hasCorrectAnswers &&
                !questionary.correctAnswers.includes(i);

              return (
                <Box
                  key={i}
                  onClick={() => handleToggle(i)}
                  sx={{ ...classes.optionRow(isMulti), ...optStyle }}
                >
                  <Box sx={classes.optionBadge(isMulti)}>
                    <Typography sx={classes.optionBadgeText}>
                      {isMulti && isSelected && !confirmed
                        ? '✓'
                        : OPTION_LETTERS[i]}
                    </Typography>
                  </Box>
                  <Typography sx={classes.optionText}>{option}</Typography>
                  {showCheck && (
                    <Typography sx={classes.optionIcon}>✓</Typography>
                  )}
                  {showCross && (
                    <Typography sx={classes.optionIcon}>✕</Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Result message */}
          {confirmed && !isCorrect && correctAnswerText && (
            <Box sx={classes.resultBox}>
              <Typography sx={classes.resultText}>
                ✕ {translate('assistant.quiz.correctAnswerIs')} &ldquo;
                {correctAnswerText}&rdquo;.
              </Typography>
            </Box>
          )}

          {/* Button */}
          {!confirmed ? (
            <Button
              onClick={handleConfirm}
              disabled={selectedOptions.length === 0}
              sx={classes.confirmButton}
            >
              {translate('assistant.quiz.confirm')}
            </Button>
          ) : (
            <Button
              onClick={() => onConfirm(selectedOptions)}
              sx={classes.nextButton}
            >
              {translate('assistant.quiz.next')}
            </Button>
          )}
        </Box>,
        document.body,
      )}
    </>
  );
};

export default withTranslation('common')(QuizScreen);
